import { Component } from '@theme/component';
import { fetchConfig, debounce, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  /** @type {Promise<void>} */
  static #changeQueue = Promise.resolve();

  /** @type {number} */
  static #pendingOperations = 0;

  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#getCartItemRowForLine(line);

    if (!lineItemRow) return;

    const lineTotalTextComponents = lineItemRow.querySelectorAll(
      '.cart-items__line-total-mobile text-component, .cart-items__line-total text-component'
    );
    const textComponent = /** @type {TextComponent | undefined} */ (
      Array.from(lineTotalTextComponents).find((el) => {
        const host = el.closest('.cart-items__line-total-mobile, .cart-items__line-total');
        return host instanceof HTMLElement && host.offsetParent !== null;
      })
    );
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    CartItemsComponent.#changeQueue = CartItemsComponent.#changeQueue
      .then(() => this.#performUpdateQuantity(config))
      .catch((error) => {
        console.error(error);
      });

    return CartItemsComponent.#changeQueue;
  }

  /**
   * @param {Object} config
   * @param {number} config.line
   * @param {number} config.quantity
   * @param {string} config.action
   */
  async #performUpdateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    const { line, quantity } = config;
    const isRemoval = quantity === 0;

    this.#setCartBusy(true);
    this.#setLineQuantityLoading(line, true);

    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    try {
      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const responseText = await response.text();
      const parsedResponseText = JSON.parse(responseText);

      resetShimmer(this);

      if (parsedResponseText.errors) {
        this.#handleCartError(line, parsedResponseText);
        return;
      }

      const newSectionHTML = new DOMParser().parseFromString(
        parsedResponseText.sections[this.sectionId],
        'text/html'
      );

      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      if (isRemoval) {
        await this.#animateRowRemoval(line);
      }

      this.#clearRemovingRows();

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponseText.sections,
        })
      );

      morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
    } finally {
      this.#setLineQuantityLoading(line, false);
      this.#setCartBusy(false);
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Plays remove-row animation after the server confirms deletion, then morph updates the DOM.
   * @param {number} line
   */
  async #animateRowRemoval(line) {
    const row = this.#getCartItemRowForLine(line);
    if (!row || prefersReducedMotion()) return;

    row.style.setProperty('--row-height', `${row.clientHeight}px`);
    row.classList.add('removing');

    const animationDurationMs = 400;

    await new Promise((resolve) => {
      const onAnimationEnd = () => {
        row.removeEventListener('animationend', onAnimationEnd);
        resolve();
      };

      row.addEventListener('animationend', onAnimationEnd);
      window.setTimeout(resolve, animationDurationMs);
    });
  }

  /**
   * @param {number} line
   * @returns {HTMLTableRowElement | null}
   */
  #getCartItemRowForLine(line) {
    const row = this.querySelector(`tr.cart-items__table-row[data-line="${line}"]`);
    return row instanceof HTMLTableRowElement ? row : null;
  }

  /**
   * Clears in-flight remove animations so section morph matches server markup.
   */
  #clearRemovingRows() {
    for (const row of this.querySelectorAll('.cart-items__table-row.removing')) {
      row.classList.remove('removing');
      row.style.removeProperty('--row-height');
    }

    for (const row of this.querySelectorAll('.cart-page .cart-items__table-row.is-swipe-open')) {
      row.classList.remove('is-swipe-open');

      const panel = row.querySelector('.cart-items__swipe-panel');
      if (panel instanceof HTMLElement) {
        panel.style.removeProperty('transform');
        panel.style.removeProperty('transition');
      }
    }
  }

  /**
   * @param {boolean} busy
   */
  #setCartBusy(busy) {
    if (busy) {
      CartItemsComponent.#pendingOperations += 1;
    } else {
      CartItemsComponent.#pendingOperations = Math.max(0, CartItemsComponent.#pendingOperations - 1);
    }

    const isBusy = CartItemsComponent.#pendingOperations > 0;

    for (const component of document.querySelectorAll('cart-items-component')) {
      if (!(component instanceof CartItemsComponent)) continue;

      if (isBusy) {
        component.#disableCartItems();
        component.setAttribute('aria-busy', 'true');
      } else {
        component.#enableCartItems();
        component.removeAttribute('aria-busy');
      }
    }

    for (const button of document.querySelectorAll('.cart-page .cart-items__remove')) {
      if (button instanceof HTMLButtonElement) {
        button.disabled = isBusy;
      }
    }
  }

  /**
   * @param {number} line
   * @returns {HTMLElement | null}
   */
  #getQuantitySelectorForLine(line) {
    const fromRefs = this.refs.quantitySelectors[line - 1];
    if (fromRefs instanceof HTMLElement) return fromRefs;

    const input = this.querySelector(`[data-cart-line="${line}"]`);
    const selector = input?.closest('quantity-selector-component');
    return selector instanceof HTMLElement ? selector : null;
  }

  /**
   * @param {number} line
   * @param {boolean} isLoading
   */
  #setLineQuantityLoading(line, isLoading) {
    const selector = this.#getQuantitySelectorForLine(line);
    if (!selector) return;

    selector.classList.toggle('quantity-selector--loading', isLoading);
    selector.toggleAttribute('aria-busy', isLoading);

    const input = selector.querySelector('input[type="number"]');
    if (input instanceof HTMLInputElement) {
      input.disabled = isLoading;
    }

    for (const button of selector.querySelectorAll('button')) {
      if (button instanceof HTMLButtonElement) {
        button.disabled = isLoading;
      }
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.#getQuantitySelectorForLine(line);
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      this.#clearRemovingRows();
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.querySelector('.cart-page__items')?.classList.add('cart-items-disabled');
    this.querySelector('.cart-page__sidebar')?.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.querySelector('.cart-page__items')?.classList.remove('cart-items-disabled');
    this.querySelector('.cart-page__sidebar')?.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
